import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Warning, X, ShieldCheck } from "@phosphor-icons/react";

/**
 * Globaler Haftungsausschluss-Banner.
 *
 * Zeigt einen dezenten, aber dauerhaft sichtbaren Hinweis im Footer-Bereich
 * aller geschützten Seiten. Beim allerersten Besuch erscheint zusätzlich ein
 * blockierendes Modal, das ausdrücklich bestätigt werden muss. Danach wird
 * die Bestätigung in localStorage hinterlegt und der Modal taucht nicht
 * wieder auf (Banner bleibt).
 */
const STORAGE_KEY = "bb.disclaimer.v1";

function loadDisclaimer() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
  } catch {
    return null;
  }
}

function saveDisclaimer() {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ acknowledged: true, ts: new Date().toISOString(), v: 1 })
    );
  } catch {
    /* storage disabled */
  }
}

export default function DisclaimerBanner() {
  const [state, setState] = useState(() => loadDisclaimer());
  const [modalOpen, setModalOpen] = useState(!state?.acknowledged);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const onReopen = () => {
      setModalOpen(true);
      setDismissed(false);
    };
    window.addEventListener("bb:open-disclaimer", onReopen);
    return () => window.removeEventListener("bb:open-disclaimer", onReopen);
  }, []);

  const accept = () => {
    saveDisclaimer();
    setState({ acknowledged: true });
    setModalOpen(false);
  };

  return (
    <>
      {modalOpen && <DisclaimerModal onAccept={accept} />}
      {!dismissed && (
        <div
          className="fixed bottom-0 left-0 right-0 md:left-60 z-40 bg-[#FFD700]/10 border-t border-[#FFD700]/40 px-3 py-2 print-hide"
          data-testid="disclaimer-banner"
        >
          <div className="max-w-6xl mx-auto flex items-center gap-2 text-[11px] md:text-xs text-[#E8CB6B]">
            <Warning size={14} weight="fill" className="shrink-0 text-[#FFC24B]" />
            <p className="flex-1 leading-snug">
              <strong className="font-medium">Nur Informationszwecke:</strong> Alle Daten
              sind statistische Schätzungen und müssen eigenständig geprüft werden.
              Markt- &amp; Preisdaten stellen <strong>keine Kauf- oder
              Verkaufsempfehlung</strong> dar. Keine Haftung.{" "}
              <Link to="/agb" className="underline hover:text-white">
                Details (AGB §4)
              </Link>
            </p>
            <button
              onClick={() => setDismissed(true)}
              className="shrink-0 p-1 hover:bg-[#FFD700]/15 rounded-sm"
              aria-label="Hinweis für diese Sitzung ausblenden"
              data-testid="disclaimer-dismiss"
            >
              <X size={12} />
            </button>
          </div>
        </div>
      )}
    </>
  );
}

function DisclaimerModal({ onAccept }) {
  return (
    <div
      className="fixed inset-0 z-[70] bg-white/10 backdrop-blur-sm flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="disclaimer-title"
      data-testid="disclaimer-modal"
    >
      <div className="max-w-lg w-full bg-[#121212] border border-white/10 rounded-sm shadow-2xl overflow-hidden">
        <div className="px-5 pt-5 pb-3 border-b border-white/10 flex items-start gap-3">
          <span className="shrink-0 p-2 rounded-sm bg-[#FFD700]/10 text-[#FFC24B]">
            <Warning size={22} weight="fill" />
          </span>
          <div>
            <div className="eid-label">Haftungsausschluss · Bitte einmal lesen</div>
            <h2 id="disclaimer-title" className="text-xl font-medium tracking-tight mt-1">
              Bodenblick dient ausschließlich der Information
            </h2>
          </div>
        </div>

        <div className="p-5 space-y-3 text-sm text-white leading-relaxed">
          <p>
            Alle Werte in Bodenblick — NDVI, Bodenfeuchte, Ertragsindex,
            Marktpreise, Prognosen, Futterqualität und Empfehlungen — sind
            <strong> statistische Schätzungen aus Fernerkundungs-, Wetter- und
            Marktmodellen</strong>. Sie ersetzen keine Vor-Ort-Beurteilung,
            keine Laboranalyse und keine professionelle Beratung.
          </p>
          <p>
            <strong>Markt- und Preisinformationen</strong> (Weizen, Mais, Soja,
            Raps etc.) stellen <strong>KEINE Kauf- oder Verkaufsempfehlung</strong>
            &nbsp;dar. Entscheidungen über Handel, Terminkontrakte oder
            Investments musst du auf Basis eigener Recherche und mit
            qualifizierter Beratung treffen.
          </p>
          <p>
            Für Richtigkeit, Vollständigkeit, Aktualität oder Verfügbarkeit
            wird <strong>keine Haftung</strong> übernommen. Eine Haftung für
            entgangene Ernten, Ertragsausfälle, Fehlinvestitionen oder
            mittelbare Schäden ist — außer bei Vorsatz oder grober Fahrlässigkeit —
            ausgeschlossen. Details regeln die{" "}
            <Link to="/agb" className="underline">
              Nutzungsbedingungen (§ 4)
            </Link>
            .
          </p>
          <div className="flex items-center gap-2 px-3 py-2 bg-[#00FA9A]/10 text-[#00FA9A] rounded-sm">
            <ShieldCheck size={16} weight="fill" />
            <span className="text-xs">
              Deine Daten werden ausschließlich serverseitig verarbeitet — keine Weitergabe an Dritte.
            </span>
          </div>
          <div className="flex items-start gap-2 px-3 py-2 bg-[#8B7CF6]/10 text-[#A99AFF] rounded-sm border border-[#8B7CF6]/40">
            <span className="font-mono text-xs uppercase tracking-wider shrink-0 mt-0.5">KI</span>
            <span className="text-xs leading-snug">
              <strong>Hinweis (EU-KI-Verordnung Art. 50):</strong> Teile der Inhalte
              auf Bodenblick — insbesondere <strong>Podcast-Folgen, Wochen-Rückblicke
              und Bildbeschreibungen</strong> — werden mithilfe generativer KI
              (Google NotebookLM / Gemini) erzeugt. Diese Inhalte sind als
              <strong> rein informativ</strong> zu verstehen, können Fehler
              enthalten und müssen vor Verwendung <strong>eigenständig geprüft</strong>
              {" "}werden.
            </span>
          </div>
        </div>

        <div className="px-5 py-4 bg-[#0A0A0A] flex items-center justify-end gap-2">
          <Link
            to="/agb"
            className="text-xs text-zinc-400 underline hover:text-white"
            data-testid="disclaimer-agb-link"
          >
            Vollständige AGB lesen
          </Link>
          <button
            onClick={onAccept}
            className="bg-[#00FA9A] hover:bg-[#33FBAE] text-white rounded-full px-5 py-2 text-sm font-medium transition-colors"
            data-testid="disclaimer-accept"
          >
            Verstanden &amp; akzeptiert
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Kleiner inline-Warnblock für Markt-/Prognose-Bereiche.
 * Kann in jede Page eingebettet werden.
 */
export function MarketDisclaimerNote({ testid = "market-disclaimer-note" }) {
  return (
    <div
      className="flex items-start gap-2 p-3 bg-[#FFD700]/10 border border-[#FFD700]/40 rounded-sm text-xs text-[#E8CB6B]"
      data-testid={testid}
    >
      <Warning size={14} weight="fill" className="shrink-0 mt-0.5 text-[#FFC24B]" />
      <div>
        <strong className="font-medium">Keine Anlageempfehlung.</strong>{" "}
        Die gezeigten Preisverläufe und Prognosen sind rein statistisch
        hochgerechnet und <strong>können nicht zur Grundlage von Handels-
        oder Investment­entscheidungen gemacht werden</strong>. Bitte alle
        Werte vor einer Nutzung eigenständig verifizieren.
      </div>
    </div>
  );
}

/**
 * Footer-Link zum erneuten Öffnen des Modals.
 */
export function ReopenDisclaimerLink({ className = "hover:text-white" }) {
  const onClick = (e) => {
    e.preventDefault();
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {}
    window.dispatchEvent(new Event("bb:open-disclaimer"));
  };
  return (
    <a
      href="#disclaimer"
      onClick={onClick}
      className={className}
      data-testid="reopen-disclaimer"
    >
      Haftungsausschluss erneut lesen
    </a>
  );
}
